/**
 * Utility functions for form value extraction and field mapping
 */

import type { FormElement, ExtractedField } from '../types';
import { normalizeBooleanValue } from '../utils/valueNormalization';

/**
 * Extract field labels from form definition for display in review UI
 */
export function extractFieldLabels(formDefinition: FormElement): Record<string, string> {
  const labels: Record<string, string> = {};

  function traverse(node: FormElement) {
    const fieldTypes = new Set(['text', 'number', 'textarea', 'radio', 'checkbox', 'single-checkbox', 'date', 'yes-no']);

    if (fieldTypes.has(node.type as string)) {
      const label = node.properties?.label || node.properties?.heading || node.properties?.name || node.id;
      labels[node.id] = label as string;
    }

    // Handle questionnaire-grid rows
    if (node.type === 'questionnaire-grid' && node.properties?.gridRows) {
      for (const row of node.properties.gridRows) {
        labels[row.id] = row.question;
      }
    }

    // Recursively process children
    if (node.children) {
      for (const child of node.children) {
        traverse(child);
      }
    }
  }

  traverse(formDefinition);
  return labels;
}

/**
 * Convert extracted values to the format expected by form renderer's data state
 * Handles type conversions and array structures for checkboxes
 */
export function convertExtractionsToFormData(
  extractions: Record<string, any>,
  formDefinition: FormElement
): Record<string, any> {
  const formData: Record<string, any> = {};
  const fieldTypeMap = buildFieldTypeMap(formDefinition);

  Object.entries(extractions).forEach(([fieldId, value]) => {
    const fieldType = fieldTypeMap[fieldId];

    if (!fieldType) {
      // Field not found in definition, skip
      return;
    }

    switch (fieldType) {
      case 'checkbox':
        // Checkboxes expect arrays
        if (Array.isArray(value)) {
          formData[fieldId] = value;
        } else if (value !== null && value !== undefined) {
          // Convert single value to array
          formData[fieldId] = [value];
        } else {
          formData[fieldId] = [];
        }
        break;

      case 'single-checkbox':
        if (value === null || value === undefined || value === '') {
          formData[fieldId] = undefined;
        } else {
          formData[fieldId] = normalizeBooleanValue(value);
        }
        break;

      case 'yes-no':
        if (value === null || value === undefined || value === '') {
          formData[fieldId] = undefined;
        } else {
          formData[fieldId] = normalizeBooleanValue(value);
        }
        break;

      case 'number':
        // Convert to number
        if (value !== null && value !== undefined) {
          const numValue = typeof value === 'number' ? value : parseFloat(String(value));
          formData[fieldId] = isNaN(numValue) ? null : numValue;
        } else {
          formData[fieldId] = null;
        }
        break;

      case 'date':
        // Keep date as string (ISO format expected)
        formData[fieldId] = value;
        break;

      default:
        // text, textarea, radio - keep as is
        formData[fieldId] = value;
        break;
    }
  });

  return formData;
}

/**
 * Build a map of fieldId -> fieldType for quick lookup
 */
function buildFieldTypeMap(formDefinition: FormElement): Record<string, string> {
  const typeMap: Record<string, string> = {};

  function traverse(node: FormElement) {
    const fieldTypes = new Set(['text', 'number', 'textarea', 'radio', 'checkbox', 'single-checkbox', 'date', 'yes-no']);

    if (fieldTypes.has(node.type as string)) {
      typeMap[node.id] = node.type as string;
    }

    // Handle questionnaire-grid rows
    if (node.type === 'questionnaire-grid' && node.properties?.gridRows) {
      const controlType = node.properties.controlType || 'radio';
      for (const row of node.properties.gridRows) {
        typeMap[row.id] = controlType;
      }
    }

    // Recursively process children
    if (node.children) {
      for (const child of node.children) {
        traverse(child);
      }
    }
  }

  traverse(formDefinition);
  return typeMap;
}

/**
 * Validate extracted values against form validation rules
 * Returns a map of fieldId -> error message (or undefined if valid)
 */
export function validateExtractions(
  extractions: Record<string, any>,
  formDefinition: FormElement
): Record<string, string | undefined> {
  const errors: Record<string, string | undefined> = {};
  const fieldValidationMap = buildFieldValidationMap(formDefinition);

  Object.entries(extractions).forEach(([fieldId, value]) => {
    const validation = fieldValidationMap[fieldId];
    if (!validation) return;

    // Check required
    if (validation.required && (value === null || value === undefined || value === '')) {
      errors[fieldId] = 'This field is required';
      return;
    }

    // Skip further validation if value is empty
    if (value === null || value === undefined || value === '') {
      return;
    }

    // Check min/max for numbers
    if (validation.min !== undefined && typeof value === 'number' && value < validation.min) {
      errors[fieldId] = `Value must be at least ${validation.min}`;
      return;
    }

    if (validation.max !== undefined && typeof value === 'number' && value > validation.max) {
      errors[fieldId] = `Value must be at most ${validation.max}`;
      return;
    }

    // Check minLength/maxLength for strings
    if (validation.minLength !== undefined && typeof value === 'string' && value.length < validation.minLength) {
      errors[fieldId] = `Must be at least ${validation.minLength} characters`;
      return;
    }

    if (validation.maxLength !== undefined && typeof value === 'string' && value.length > validation.maxLength) {
      errors[fieldId] = `Must be at most ${validation.maxLength} characters`;
      return;
    }

    // Check pattern for strings
    if (validation.pattern && typeof value === 'string') {
      try {
        const regex = new RegExp(validation.pattern);
        if (!regex.test(value)) {
          errors[fieldId] = validation.patternMessage || 'Invalid format';
          return;
        }
      } catch (e) {
        console.error('Invalid regex pattern:', validation.pattern);
      }
    }
  });

  return errors;
}

/**
 * Build a map of fieldId -> validation rules
 */
interface ValidationRules {
  required?: boolean;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  patternMessage?: string;
}

function buildFieldValidationMap(formDefinition: FormElement): Record<string, ValidationRules> {
  const validationMap: Record<string, ValidationRules> = {};

  function traverse(node: FormElement) {
    const fieldTypes = new Set(['text', 'number', 'textarea', 'radio', 'checkbox', 'single-checkbox', 'date', 'yes-no']);

    if (fieldTypes.has(node.type as string)) {
      const rules: ValidationRules = {};

      if (node.properties?.required) {
        rules.required = true;
      }

      if (node.properties?.min !== undefined) {
        rules.min = node.properties.min as number;
      }

      if (node.properties?.max !== undefined) {
        rules.max = node.properties.max as number;
      }

      if (node.properties?.minLength !== undefined) {
        rules.minLength = node.properties.minLength as number;
      }

      if (node.properties?.maxLength !== undefined) {
        rules.maxLength = node.properties.maxLength as number;
      }

      if (node.properties?.pattern) {
        rules.pattern = node.properties.pattern as string;
      }

      // Get validation message from validationRules if available
      if (node.properties?.validationRules && Array.isArray(node.properties.validationRules)) {
        const patternRule = node.properties.validationRules.find((r: any) => r.type === 'pattern');
        if (patternRule) {
          rules.patternMessage = patternRule.message;
        }
      }

      if (Object.keys(rules).length > 0) {
        validationMap[node.id] = rules;
      }
    }

    // Handle questionnaire-grid rows
    if (node.type === 'questionnaire-grid' && node.properties?.gridRows) {
      for (const row of node.properties.gridRows) {
        if (node.properties.required) {
          validationMap[row.id] = { required: true };
        }
      }
    }

    // Recursively process children
    if (node.children) {
      for (const child of node.children) {
        traverse(child);
      }
    }
  }

  traverse(formDefinition);
  return validationMap;
}

/**
 * Filter extractions by confidence threshold
 * Returns only extractions with confidence >= threshold
 */
export function filterByConfidence(
  extractions: Record<string, ExtractedField>,
  threshold: number = 0.5
): Record<string, ExtractedField> {
  const filtered: Record<string, ExtractedField> = {};

  Object.entries(extractions).forEach(([fieldId, extraction]) => {
    if (extraction.confidence >= threshold) {
      filtered[fieldId] = extraction;
    }
  });

  return filtered;
}

/**
 * Get fields with low confidence (< threshold)
 * Useful for identifying fields that may need manual review
 */
export function getLowConfidenceFields(
  extractions: Record<string, ExtractedField>,
  threshold: number = 0.7
): string[] {
  return Object.entries(extractions)
    .filter(([_, extraction]) => extraction.confidence < threshold)
    .map(([fieldId, _]) => fieldId);
}
